import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { ICreditorRepository } from '@app/domain/ports';
import type { Creditor } from '@app/domain/entities';
import type { CreditorFilter, CreditorCursor } from '@app/domain/ports';

interface CreditorRow {
  id: number;
  date: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  categoryId: number;
  categoryName: string;
  currencyCode: string;
  amount: number;
  totalAmount: number;
  exchangeRate: number;
  isDebtClosed: number; // SQLite boolean (0 or 1)
  note: string;
}

export class CreditorRepository extends BaseSqliteRepository implements ICreditorRepository {
  private readonly BASE_FROM = `
    FROM creditors cr
    INNER JOIN accounts a ON cr.account_id = a._id
    INNER JOIN categories cat ON cr.category_id = cat._id
    INNER JOIN currencies c ON cr.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE cr.is_deleted = 0
  `;

  private readonly BASE_FIELDS = [
    'cr._id as id',
    'cr.creditor_date as date',
    'cr.account_id as accountId',
    'a.name as accountName',
    'cr.category_id as categoryId',
    'cat.name as categoryName',
    'cl.ticker as currencyCode',
    'cr.amount as amount',
    'cr.total_amount as totalAmount',
    'cr.exchange_rate as exchangeRate',
    'cr.is_debt_closed as isDebtClosed',
    'cr.note as note',
  ];

  private readonly COUNT_QUERY = `
    SELECT COUNT(*) AS total
    ${this.BASE_FROM}
  `;

  async count(filter?: CreditorFilter): Promise<number> {
    const { query, params } = this.applyFilter(this.COUNT_QUERY, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async findAll(cursor: CreditorCursor, filter?: CreditorFilter): Promise<Creditor[]> {
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
    `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter);

    query += ` ORDER BY cr.creditor_date DESC, cr._id DESC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<CreditorRow>(query, params);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async getById(id: number): Promise<Creditor | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
       AND cr._id = ?`;
    const row = await this.queryOne<CreditorRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async insert(creditor: Omit<Creditor, 'id' | 'accountName' | 'creditorName'>): Promise<number> {
    const date = new Date(creditor.date).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO creditors (creditor_date, account_id, category_id, amount, total_amount, currency_id, exchange_rate, is_debt_closed, note)
       VALUES (?, ?, ?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?, ?)
       RETURNING _id AS id`,
      [
        date,
        creditor.accountId,
        creditor.creditorId,
        creditor.amount,
        creditor.totalAmount,
        creditor.currencyCode,
        creditor.exchangeRate,
        creditor.isDebtClosed ? 1 : 0,
        creditor.note,
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO creditors', [], 'Failed to insert creditor');
    }

    return result.id;
  }

  async update(creditor: Omit<Creditor, 'accountName' | 'categoryName'>): Promise<void> {
    const date = new Date(creditor.date).setUTCHours(0, 0, 0, 0) / 1000;

    await this.execute(
      `UPDATE creditors
       SET creditor_date = ?,
           account_id = ?,
           category_id = ?,
           amount = ?,
           total_amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           exchange_rate = ?,
           is_debt_closed = ?,
           note = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [
        date,
        creditor.accountId,
        creditor.creditorId,
        creditor.amount,
        creditor.totalAmount,
        creditor.currencyCode,
        creditor.exchangeRate,
        creditor.isDebtClosed ? 1 : 0,
        creditor.note,
        creditor.id,
      ],
    );
  }

  async updateTotalAmount(id: number, totalAmount: number): Promise<void> {
    await this.execute(
      `UPDATE creditors
       SET total_amount = ?,
           is_debt_closed = CASE WHEN ? >= amount THEN 1 ELSE 0 END,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [totalAmount, totalAmount, id],
    );
  }

  async refreshDebtClosed(id: number): Promise<boolean> {
    // Only reopen — never auto-close; user decides that
    await this.execute(
      `UPDATE creditors
       SET is_debt_closed = CASE WHEN total_amount < amount THEN 0 ELSE is_debt_closed END,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [id],
    );

    // Return true when closing should be suggested (paid back >= principal, but not yet closed)
    const result = await this.queryOne<{ suggest: number }>(
      `SELECT CASE WHEN total_amount >= amount AND is_debt_closed = 0 THEN 1 ELSE 0 END AS suggest
       FROM creditors WHERE _id = ?`,
      [id],
    );
    return result?.suggest === 1;
  }

  async setDebtClosed(id: number, closed: boolean): Promise<void> {
    await this.execute(`UPDATE creditors SET is_debt_closed = ?, updated_at = strftime('%s','now') WHERE _id = ?`, [
      closed ? 1 : 0,
      id,
    ]);
  }

  async delete(id: number): Promise<void> {
    await this.execute("UPDATE creditors SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [id]);
  }

  private applyFilter(
    query: string,
    filter?: CreditorFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountId) {
      query += ` AND cr.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.creditorId) {
      query += ` AND cr.category_id = ?`;
      params.push(filter.creditorId);
    }

    if (filter?.isDebtClosed !== undefined) {
      query += ` AND cr.is_debt_closed = ?`;
      params.push(filter.isDebtClosed ? 1 : 0);
    }

    if (filter?.dateFrom) {
      query += ` AND cr.creditor_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND cr.creditor_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.currencyCode) {
      query += ` AND cl.ticker = ?`;
      params.push(filter.currencyCode);
    }

    if (filter?.hasOutstandingAmount !== undefined) {
      query += filter.hasOutstandingAmount ? ` AND cr.amount > cr.total_amount` : ` AND cr.amount <= cr.total_amount`;
    }

    return { query, params };
  }

  async countByAccount(accountId: number): Promise<number> {
    const result = await this.queryOne<{ count: number }>(
      `SELECT COUNT(*) as count FROM creditors WHERE account_id = ? AND is_deleted = 0`,
      [accountId],
    );
    return result?.count ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE creditors
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  private mapRowToEntity(row: CreditorRow): Creditor {
    return {
      id: row.id,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      creditorId: row.categoryId,
      creditorName: row.categoryName,
      currencyCode: row.currencyCode,
      amount: row.amount,
      totalAmount: row.totalAmount,
      exchangeRate: !row.exchangeRate ? 1 : row.exchangeRate,
      isDebtClosed: row.isDebtClosed === 1,
      note: row.note,
    };
  }
}
